import {useConfig} from '@aboutyou/config';
import {Sponsored} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_sponsored.types';
import {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackBASMonitoring,
  TrackPromotions,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  BrandLogo,
  COLORS,
  desktopAndUpLinaria,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  largeScreensLinaria,
  mediaTabletAndUp,
  ProductImage,
  tabletAndDownLinaria,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {STREAM_TOP_SPACING} from '../../backend-services/Header/constants';
import {
  Content,
  Header,
  HorizontalSponsoredPlacementContainer,
} from '../SponsoredPlacement/HorizontalSponsoredPlacementContainer';
import {hasEnoughSponsoredProducts} from '../SponsoredPlacement/utils/productThreshold';
import {useSponsoredBannerTrackingKey} from './utils/useSponsoredBannerTrackingKey';

export const SponsoredBanner: React.FC<SponsoredContentSection> = ({
  color,
  headline,
  image,
  video,
  promotionTracking,
  sponsored,
  brand,
  targetUrl,
  productImages,
}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();
  const trackPromotionKey = useSponsoredBannerTrackingKey(
    promotionTracking.promotionId,
  );
  const isMobile = device === 'mobile';
  const hasEnoughProducts = hasEnoughSponsoredProducts({
    placement: 'header',
    device: isMobile ? 'mobile' : 'desktop',
    count: productImages.length,
  });

  const stableVideo = trackPromotionKey ? video : undefined;
  const visibleProductImages = isMobile
    ? productImages.slice(0, 2)
    : productImages;

  if (!hasEnoughProducts) {
    return (
      <TrackBASMonitoring
        basPromotionSource={TrackingPromotionCreative.BasCategoryHeader}
        monitoringId="insufficient_products_category_header"
      />
    );
  }

  return (
    <TrackPromotions
      key={trackPromotionKey}
      disabled={!trackPromotionKey}
      promotion={{
        advertisement: {
          id: Number(promotionTracking.destination?.split('|')[1]),
          name: promotionTracking.promotionName,
          path: targetUrl.url,
          promotionId: promotionTracking.promotionId,
          locationId: promotionTracking.locationId,
          creativeName: promotionTracking.creativeName,
          creativeSlot: promotionTracking.creativeSlot,
        },
        source: TrackingPromotionCreative.BasCategoryHeader,
      }}
      streamPosition={0}
      threshold={0.5}
      triggerOnce
    >
      <StyledHorizontalSponsoredPlacementContainer
        contentAlignment="start"
        color={color}
        image={image}
        video={stableVideo}
        targetUrl={targetUrl.url}
        onClick={() => {
          trackOriginClick(promotionTracking.locationId as originClick)();
        }}
        promotionTracking={promotionTracking}
        brand={brand}
        sponsored={sponsored}
      >
        <Wrapper>
          <Headline $hasBrand={!!brand} color="White">
            {headline}
          </Headline>
          <ProductList>
            {visibleProductImages.map((image, index) => (
              <StyledProductImage
                key={`${image.url}-${index}`}
                src={image.url}
                alt={image.description}
                widths={[120, 240, 480]}
                breakpoints={[{columns: 5}]}
                padded
              />
            ))}
            <SeeMoreTile aria-hidden="true">
              <Icon
                asset={GenericIconName.RightArrowSharpEdges}
                size="S"
                fill={color.hex}
              />
            </SeeMoreTile>
          </ProductList>
        </Wrapper>
        {brand && (
          <StyledBrandLogo
            $hasImage={!!brand.deprecatedWhiteLogoImage}
            name={brand.name}
            image={brand.deprecatedWhiteLogoImage}
            maxHeight="40px"
            showTooltip={false}
          />
        )}
      </StyledHorizontalSponsoredPlacementContainer>
    </TrackPromotions>
  );
};

const StyledHorizontalSponsoredPlacementContainer = styled(
  HorizontalSponsoredPlacementContainer,
)<{sponsored?: Sponsored}>`
  aspect-ratio: 4 / 1.5;
  border-radius: 4px;

  @media ${mediaTabletAndUp} {
    aspect-ratio: 5 / 1;
    border-radius: 4px;
    margin-bottom: ${STREAM_TOP_SPACING}px;
  }

  ${Header} {
    --spacing: 0px;

    @media ${mediaTabletAndUp} {
      --spacing: 4px;
    }
  }

  ${Content} {
    top: 0;
    padding-block: 0;
    padding: 4px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  width: min(500px, 50%);
  min-width: 0;

  ${tabletAndDownLinaria(`
    padding-left: 6px;
    gap: 0px;
  `)}

  ${largeScreensLinaria(`
    gap: 24px;
    margin-bottom: 6px;
  `)}
`;

const Headline = styled(Text)<{$hasBrand: boolean}>`
  display: ${props => (props.$hasBrand ? 'none' : 'block')};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${tabletAndUpLinaria(`
    display: block;
  `)}

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 1.2;
  `)}

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px24};
  `)}
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 100%;
  height: 60%;
  max-height: 120px;

  ${tabletAndUpLinaria(`
    grid-template-columns: repeat(6, 1fr);
  `)}
`;

const StyledProductImage = styled(ProductImage)`
  height: 100%;
  && {
    > img {
      max-width: 80%;
      max-height: 80%;
    }
  }
`;

const SeeMoreTile = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: ${COLORS.ProductImageBackground};
`;

const StyledBrandLogo = styled(BrandLogo)<{$hasImage: boolean}>`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: auto;
  height: auto;
  z-index: 1;
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.heavy};
  max-width: ${props => (props.$hasImage ? '30%' : 'min-content')};

  ${tabletAndUpLinaria(`
    bottom: 16px;
    right: 16px;
    max-width: unset;
    height: 24px;
    font-size: ${FONT_SIZES.px24};
    `)}

  ${tabletAndDownLinaria(`
    bottom: 10px;
    right: 10px;
  img {
    max-height: 32px;
    }
`)}


  ${desktopAndUpLinaria(`
    height: 40px;
    font-size: ${FONT_SIZES.px32};
  `)}
`;
